import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import { InpatientDischargeDiagnosis } from '../types';

interface InpatientDischargeDiagnosisTableProps {
  /**
   * The rows, not the HTTP envelope. Taking the response meant taking it optionally, and an
   * absent response then rendered the same "nothing recorded" copy as a response that genuinely
   * reported none — two different answers collapsed into one sentence. ChartFactory already
   * renders a placeholder when there is no response at all, so that state never reaches here.
   */
  diagnoses: InpatientDischargeDiagnosis[];
  title: string;
}

/**
 * SHER-583. The ICD-11 diagnoses recorded on the discharge form, for inpatient episodes that
 * ENDED in the selected period.
 *
 * Deliberately separate from the inpatient diagnosis donut beside it, which answers a different
 * question: that one counts any ICD-coded record in the window, admission notes included. An
 * admission diagnosis is provisional; a discharge diagnosis is the clinical conclusion after the
 * stay, which is what the ticket asks to report on. The subtitle says so on screen, because two
 * diagnosis panels on one tab showing different numbers otherwise reads as a bug.
 */
const InpatientDischargeDiagnosisTable: React.FC<InpatientDischargeDiagnosisTableProps> = ({
  diagnoses,
  title,
}) => (
    <Box>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        <Translate id="hospital.analytics.discharge_diagnoses.subtitle" />
      </Typography>

      {diagnoses.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          <Translate id="hospital.analytics.discharge_diagnoses.empty" />
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Translate id="hospital.analytics.discharge_diagnoses.diagnosis" /></TableCell>
                <TableCell><Translate id="hospital.analytics.discharge_diagnoses.code" /></TableCell>
                <TableCell align="right"><Translate id="hospital.analytics.discharge_diagnoses.discharges" /></TableCell>
                <TableCell><Translate id="hospital.analytics.discharge_diagnoses.services" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {diagnoses.map((diagnosis) => (
                <TableRow key={diagnosis.code}>
                  <TableCell>{diagnosis.name}</TableCell>
                  <TableCell>{diagnosis.code}</TableCell>
                  <TableCell align="right">{diagnosis.discharges}</TableCell>
                  {/* Ordered by how many discharges each service contributed, not
                      alphabetically, so "main services" means what it says. Capped at three:
                      a diagnosis seen across a whole hospital would otherwise wrap the row. */}
                  <TableCell>{diagnosis.departments.slice(0, 3).join(', ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
  </Box>
);

export default InpatientDischargeDiagnosisTable;
